import { isEmpty } from "./isType";

import type { HttpTypes } from "@medusajs/types";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";

export const getPercentageDiff = (original: number, calculated: number) => {
  const diff = original - calculated;
  const decrease = (diff / original) * 100;

  return decrease.toFixed();
};

export type FormattedProductVariantPrice = {
  calculated_price_number: number;
  calculated_price: string;
  original_price_number: number | null;
  original_price: string;
  currency_code: string | null;
  price_type: string | null;
  percentage_diff: string;
};

// Overload signatures for better type inference
export function getPricesForVariant<
  T extends {
    calculated_price: SalonStockerHttpTypes.StoreProductCalculatedPriceSet;
  }
>(variant: T): FormattedProductVariantPrice;
export function getPricesForVariant<
  T extends {
    calculated_price: SalonStockerHttpTypes.StoreProductCalculatedPriceSet;
  }
>(variant: T | undefined): FormattedProductVariantPrice | null;
export function getPricesForVariant<
  T extends {
    calculated_price: SalonStockerHttpTypes.StoreProductCalculatedPriceSet;
  }
>(variant?: T): FormattedProductVariantPrice | null {
  if (!variant) {
    return null;
  }

  return {
    calculated_price_number: variant.calculated_price.calculated_amount,
    calculated_price: convertToLocale({
      amount: variant.calculated_price.calculated_amount,
      currency_code: variant.calculated_price.currency_code
    }),
    original_price_number: variant.calculated_price.original_amount,
    original_price: convertToLocale({
      amount: variant.calculated_price.original_amount,
      currency_code: variant.calculated_price.currency_code
    }),
    currency_code: variant.calculated_price.currency_code,
    price_type:
      variant.calculated_price.calculated_price?.price_list_type ?? null,
    percentage_diff: getPercentageDiff(
      variant.calculated_price.original_amount,
      variant.calculated_price.calculated_amount
    )
  } satisfies FormattedProductVariantPrice;
}

export function getProductPrice<
  T extends {
    variants: HttpTypes.StoreProductVariant[];
    id: string;
  }
>({
  //
  product,
  variantId
}: {
  product: T;
  variantId?: string;
}) {
  const cheapestPrice = () => {
    const cheapestVariant: any = product.variants
      .filter((v: any) => !!v.calculated_price)
      .sort((a: any, b: any) => {
        return (
          a.calculated_price.calculated_amount -
          b.calculated_price.calculated_amount
        );
      })[0];

    return getPricesForVariant(cheapestVariant);
  };

  const variantPrice = () => {
    if (!variantId) {
      return null;
    }

    const variant: any = product.variants.find(
      (v) => v.id === variantId || v.sku === variantId
    );

    if (!variant) {
      return null;
    }

    return getPricesForVariant(variant);
  };

  return {
    product,
    cheapestPrice: cheapestPrice(),
    variantPrice: variantPrice()
  };
}

type ConvertToLocaleParams = {
  amount: number;
  currency_code?: string | null;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  locale?: string;
};

export const formatPrice = (amount: number) => convertToLocale({ amount });

export function convertToLocale({
  amount,
  currency_code = "AUD",
  minimumFractionDigits = 2,
  maximumFractionDigits,
  locale = "en-AU"
}: ConvertToLocaleParams) {
  return currency_code && !isEmpty(currency_code)
    ? new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency_code,
        minimumFractionDigits,
        maximumFractionDigits
      }).format(amount)
    : amount.toString();
}

// Currencies whose smallest unit IS the major unit — a Stripe amount for these
// must NOT be multiplied by 100. Lives here, beside convertToLocale, so
// utils/money is the single owner of both directions of the major<->minor
// conversion: format for display, and toMinorUnits for the payment gateway.
export const noDivisionCurrencies = new Set([
  "krw",
  "jpy",
  "vnd",
  "clp",
  "pyg",
  "xaf",
  "xof",
  "bif",
  "djf",
  "gnf",
  "kmf",
  "mga",
  "rwf",
  "xpf",
  "htg",
  "vuv",
  "xag",
  "xdr",
  "xau"
]);

// Convert a major-unit amount (e.g. dollars) to the integer minor units Stripe
// bills in (e.g. cents), honouring zero-decimal currencies. The inverse of
// convertToLocale/formatPrice, and the only place that knows the *100 rule.
export function toMinorUnits(amount: number, currencyCode: string): number {
  const isZeroDecimal = noDivisionCurrencies.has(currencyCode.toLowerCase());
  return Math.round(isZeroDecimal ? amount : amount * 100);
}
